// components/ui/Breadcrumbs.jsx — single-line breadcrumb path primitive.
//
// Generalized form of the inline "Course / Domain / Concept" pattern that
// drifted across course-deep pages. Use this instead of bespoke
// "back arrow + title" rows or hand-rolled breadcrumb spans.
//
// Usage:
//   <Breadcrumbs items={[
//     { label: 'My Courses', to: '/my-courses' },
//     { label: course.name,  to: `/course/${courseId}` },
//     { label: 'Mastery' },   // last item: no `to` = current page
//   ]} />
//
// Render rules:
//   - segment with `to` → <Link>; without `to` → plain <span>
//   - last item is bolded (current page); earlier items muted
//   - separators are gray-300 slashes
//   - no internal padding — caller controls vertical rhythm
import React from 'react';
import { Link } from 'react-router-dom';

export default function Breadcrumbs({ items = [], className = '' }) {
  // Filter out items with no label (e.g. while async data is still loading
  // and `course?.name` is undefined). Keeps the row from showing "/  /".
  const visible = items.filter((it) => it?.label);
  if (visible.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-2 text-sm min-w-0 ${className}`}
    >
      {visible.map((item, i) => {
        const isLast = i === visible.length - 1;
        // The root never shrinks; middle crumbs cap and truncate; the current page takes what is left.
        const fit = i === 0 ? 'flex-shrink-0' : isLast ? 'min-w-0 truncate' : 'min-w-0 max-w-[14rem] truncate';
        const key = `${i}-${item.label}`;
        return (
          <React.Fragment key={key}>
            {i > 0 && (
              <span className="text-faint flex-shrink-0" aria-hidden="true">/</span>
            )}
            {item.to && !isLast ? (
              <Link
                to={item.to}
                className={`text-mutedink hover:text-read transition-colors ${fit}`}
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={`${isLast ? 'font-semibold text-ink' : 'text-mutedink'} ${fit}`}
                title={item.label}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
